import { createAction } from "@reduxjs/toolkit";
import _ from "lodash";
import axios from "axios";
import { takeEvery, call, put, select } from "redux-saga/effects";
import buildUrl from "build-url-ts";

import {
  AddAssetToCourseRequest,
  Course,
  CreateAssessmentRequest,
  CreateCourseErrorResponse,
  CreateCourseRequest,
  CreateCourseResponse,
  CreateQuestionInAssessmentRequest,
  DeleteCourseErrorResponse,
  DeleteCourseResponse,
  DeleteQuestionInAssessmentRequest,
  DiscardCourseDraftErrorResponse,
  DiscardCourseDraftResponse,
  FetchCourseErrorResponse,
  FetchCourseResponse,
  FetchDraftCoursesErrorResponse,
  FetchDraftCoursesRequest,
  FetchDraftCoursesResponse,
  FetchPublishedCoursesErrorResponse,
  FetchPublishedCoursesRequest,
  FetchPublishedCoursesResponse,
  PublishCourseErrorResponse,
  PublishCourseResponse,
  RearrangeEntitiesInCourseRequest,
  RearrangeQuestionsInAssessmentRequest,
  RemoveEntityFromCourseRequest,
  UpdateAssessmentPropertyRequest,
  UpdateCourseErrorResponse,
  UpdateCoursePropertyRequest,
  UpdateCourseRequest,
  UpdateCourseResponse,
  UpdateLessonPropertyRequest,
  UpdateQuestionInAssessmentRequest,
  UpdateValidationErrorsForCourseRequest,
  GenerateQuestionsRequest,
  GenerateQuestionsResponse,
  GenerateQuestionsErrorResponse,
  FetchLessonForCourseRequest,
  FetchLessonForCourseResponse,
  FetchLessonForCourseErrorResponse,
  FetchLessonPreviewForCourseRequest,
  FetchLessonPreviewForCourseErrorResponse,
  FetchLessonPreviewForCourseResponse,
  SearchCoursesRequest,
  SearchCoursesResponse,
  SearchCoursesErrorResponse,
} from "src/types/coaching/courses";

import {
  ADD_ASSET_TO_COURSE,
  CREATE_ASSESSMENT_REQUEST,
  CREATE_COURSE_REQUEST,
  CREATE_QUESTION_IN_ASSESSMENT_REQUEST,
  CREATING_COURSE_FAILED,
  CREATING_COURSE_SUCCEEDED,
  DELETE_COURSE_CONTENT,
  DELETE_COURSE_REQUEST,
  DELETE_QUESTION_IN_ASSESSMENT_REQUEST,
  DELETING_COURSE_FAILED,
  DELETING_COURSE_SUCCEEDED,
  DISCARD_COURSE_DRAFT_REQUEST,
  DISCARDING_COURSE_DRAFT_FAILED,
  DISCARDING_COURSE_DRAFT_SUCCEEDED,
  EDIT_COURSE_REQUEST,
  FETCH_COURSE_REQUEST,
  FETCH_DRAFT_COURSE_REQUEST,
  FETCH_LESSON_FOR_COURSE_REQUEST,
  FETCH_LESSON_PREVIEW_FOR_COURSE_REQUEST,
  FETCHING_COURSE_FAILED,
  FETCHING_COURSE_SUCCEEDED,
  FETCHING_DRAFT_COURSE_FAILED,
  FETCHING_DRAFT_COURSE_SUCCEEDED,
  FETCHING_DRAFT_COURSES_FAILED,
  FETCHING_DRAFT_COURSES_REQUEST,
  FETCHING_DRAFT_COURSES_SUCCEEDED,
  FETCHING_LESSON_FOR_COURSE_FAILED,
  FETCHING_LESSON_FOR_COURSE_SUCCEEDED,
  FETCHING_LESSON_PREVIEW_FOR_COURSE_FAILED,
  FETCHING_LESSON_PREVIEW_FOR_COURSE_SUCCEEDED,
  FETCHING_PUBLISHED_COURSES_FAILED,
  FETCHING_PUBLISHED_COURSES_REQUEST,
  FETCHING_PUBLISHED_COURSES_SUCCEEDED,
  GENERATE_QUESTIONS_REQUEST,
  GENERATING_QUESTIONS_FAILED,
  GENERATING_QUESTIONS_SUCCEEDED,
  INVALIDATE_COURSE_CREATION_STATE,
  INVALIDATE_COURSE_DELETION_STATE,
  INVALIDATE_COURSE_UPDATE_STATE,
  INVALIDATE_COURSES,
  INVALIDATE_GENERATE_QUESTIONS,
  INVALIDATE_PUBLISH_COURSE,
  PUBLISH_COURSE_REQUEST,
  PUBLISHING_COURSE_FAILED,
  PUBLISHING_COURSE_SUCCEEDED,
  REARRANGE_ENTITIES_IN_COURSE,
  REARRANGE_QUESTIONS_IN_ASSESSMENT_REQUEST,
  REMOVE_ENTITY_FROM_COURSE,
  UPDATE_ASSESSMENT_PROPERTY_REQUEST,
  UPDATE_COURSE_PROPERTY_REQUEST,
  UPDATE_LESSON_PROPERTY_REQUEST,
  UPDATE_QUESTION_IN_ASSESSMENT_REQUEST,
  UPDATE_VALIDATION_ERRORS_FOR_COURSE,
  UPDATING_COURSE_FAILED,
  UPDATING_COURSE_REQUEST,
  UPDATING_COURSE_SUCCEEDED,
  VALIDATE_COURSE_REQUEST,
  SEARCH_COURSES_REQUEST,
  SEARCHING_COURSES_SUCCEEDED,
  SEARCHING_COURSES_FAILED,
} from "../actionTypes";
import { validateCourseRequest } from "src/pages/coaching/manage/courses/edit/helper";

// eslint-disable-next-line no-undef
const apiUrl = `${process.env.REACT_APP_COACHING_API_URL}`;


// fetch draft courses
export const fetchDraftCourses = createAction<FetchDraftCoursesRequest>(
  FETCHING_DRAFT_COURSES_REQUEST,
);
const fetchingDraftCoursesSucceeded = createAction<FetchDraftCoursesResponse>(
  FETCHING_DRAFT_COURSES_SUCCEEDED,
);
const fetchingDraftCoursesFailed = createAction<FetchDraftCoursesErrorResponse>(
  FETCHING_DRAFT_COURSES_FAILED,
);

const fetchDraftCoursesSaga = function* (
  action: ReturnType<typeof fetchDraftCourses>,
): any {
  const request = action.payload as FetchDraftCoursesRequest;

  const fetchCoursesApi = () => {
    return axios({
      method: "GET",
      url: `${apiUrl}/api/1.0/courses/drafts`,
      params: request,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchCoursesApi);
    yield put(
      fetchingDraftCoursesSucceeded({
        response: response,
      }),
    );
  } catch (error) {
    yield put(fetchingDraftCoursesFailed({ error: error }));
  }
};

// fetch draft courses
export const fetchPublishedCourses = createAction<FetchPublishedCoursesRequest>(
  FETCHING_PUBLISHED_COURSES_REQUEST,
);
const fetchingPublishedCoursesSucceeded =
  createAction<FetchPublishedCoursesResponse>(
    FETCHING_PUBLISHED_COURSES_SUCCEEDED,
  );
const fetchingPublishedCoursesFailed =
  createAction<FetchPublishedCoursesErrorResponse>(
    FETCHING_PUBLISHED_COURSES_FAILED,
  );

const fetchPublishedCoursesSaga = function* (
  action: ReturnType<typeof fetchPublishedCourses>,
): any {
  const request = action.payload as FetchPublishedCoursesRequest;

  const fetchCoursesApi = () => {
    return axios({
      method: "GET",
      url: `${apiUrl}/api/1.0/courses/published`,
      params: request,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchCoursesApi);
    yield put(
      fetchingPublishedCoursesSucceeded({
        response: response,
      }),
    );
  } catch (error) {
    yield put(fetchingPublishedCoursesFailed({ error: error }));
  }
};

// search courses
export const searchCourses = createAction<SearchCoursesRequest>(SEARCH_COURSES_REQUEST);
const searchingCoursesSucceeded = createAction<SearchCoursesResponse>(SEARCHING_COURSES_SUCCEEDED);
const searchingCoursesFailed = createAction<SearchCoursesErrorResponse>(SEARCHING_COURSES_FAILED);

const searchCoursesSaga = function* (action: ReturnType<typeof searchCourses>): any {
  const request = action.payload as SearchCoursesRequest;

  const fetchPublicCoursesApi = () => {
    return axios({
      method: "GET",
      url: `${apiUrl}/api/1.0/courses/search`,
      params: request,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchPublicCoursesApi);
    yield put(
      searchingCoursesSucceeded({
        response: response
      })
    );
  } catch (error) {
    yield put(searchingCoursesFailed({ error: error }));
  }
};

// create course
export const createCourse = createAction<CreateCourseRequest>(
  CREATE_COURSE_REQUEST,
);
const creatingCourseSucceeded = createAction<CreateCourseResponse>(
  CREATING_COURSE_SUCCEEDED,
);
const creatingCourseFailed = createAction<CreateCourseErrorResponse>(
  CREATING_COURSE_FAILED,
);

const createCourseSaga = function* (
  action: ReturnType<typeof createCourse>,
): any {
  const request = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/courses`,
  });

  const createCourseApi = () => {
    return axios({
      method: "POST",
      url: url,
      data: request,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(createCourseApi);
    yield put(creatingCourseSucceeded({ response: response }));
  } catch (error) {
    yield put(creatingCourseFailed({ error: error }));
  }
};

// fetch course
export const fetchCourse = createAction<any>(FETCH_COURSE_REQUEST);
const fetchingCourseSucceeded = createAction<FetchCourseResponse>(
  FETCHING_COURSE_SUCCEEDED,
);
const fetchingCourseFailed = createAction<FetchCourseErrorResponse>(
  FETCHING_COURSE_FAILED,
);

const fetchCourseSaga = function* (
  action: ReturnType<typeof fetchCourse>,
): any {
  const id = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/courses/${id}`,
  });

  const fetchCourseApi = () => {
    return axios({
      method: "GET",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchCourseApi);
    yield put(
      fetchingCourseSucceeded({
        response: response,
      }),
    );
  } catch (error) {
    yield put(fetchingCourseFailed({ id: id, error: error }));
  }
};

// fetch draft course
export const fetchDraftCourse = createAction<string>(
  FETCH_DRAFT_COURSE_REQUEST,
);
const fetchingDraftCourseSucceeded = createAction<FetchCourseResponse>(
  FETCHING_DRAFT_COURSE_SUCCEEDED,
);
const fetchingDraftCourseFailed = createAction<FetchCourseErrorResponse>(
  FETCHING_DRAFT_COURSE_FAILED,
);

const fetchDraftCourseSaga = function* (
  action: ReturnType<typeof fetchDraftCourse>,
): any {
  const id = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/courses/${id}/draft`,
  });

  const fetchCourseApi = () => {
    return axios({
      method: "GET",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchCourseApi);
    yield put(
      fetchingDraftCourseSucceeded({
        response: response,
      }),
    );
  } catch (error) {
    yield put(fetchingDraftCourseFailed({ id: id, error: error }));
  }
};

// update course
export const updateCourse = createAction<UpdateCourseRequest>(
  UPDATING_COURSE_REQUEST,
);
const updatingCourseSucceeded = createAction<UpdateCourseResponse>(
  UPDATING_COURSE_SUCCEEDED,
);
const updatingCourseFailed = createAction<UpdateCourseErrorResponse>(
  UPDATING_COURSE_FAILED,
);

const updateCourseSaga = function* (
  action: ReturnType<typeof updateCourse>,
): any {
  const { id, request } = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/courses/${id}`,
  });

  const updateCourseApi = () => {
    return axios({
      method: "PUT",
      url: url,
      data: request,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(updateCourseApi);
    yield put(updatingCourseSucceeded({ response: response }));
  } catch (error) {
    yield put(updatingCourseFailed({ id: id, error: error }));
  }
};

// delete course
export const deleteCourse = createAction<string>(DELETE_COURSE_REQUEST);
const deletingCourseSucceeded = createAction<DeleteCourseResponse>(DELETING_COURSE_SUCCEEDED);
const deletingCourseFailed = createAction<DeleteCourseErrorResponse>(DELETING_COURSE_FAILED);

const deleteCourseSaga = function* (action: ReturnType<typeof deleteCourse>): any {
  const id = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/courses/${id}`,
  });

  const deleteCourseApi = () => {
    return axios({
      method: "DELETE",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(deleteCourseApi);
    yield put(deletingCourseSucceeded({ id: id, response: response }));
  } catch (error) {
    yield put(deletingCourseFailed({ id: id, error: error }));
  }
};

// discard course draft
export const discardCourseDraft = createAction<string>(
  DISCARD_COURSE_DRAFT_REQUEST,
);
const discardingCourseDraftSucceeded = createAction<DiscardCourseDraftResponse>(
  DISCARDING_COURSE_DRAFT_SUCCEEDED,
);
const discardingCourseDraftFailed =
  createAction<DiscardCourseDraftErrorResponse>(DISCARDING_COURSE_DRAFT_FAILED);

const discardCourseDraftSaga = function* (
  action: ReturnType<typeof discardCourseDraft>,
): any {
  const id = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/courses/${id}/draft`,
  });

  const discardCourseApi = () => {
    return axios({
      method: "DELETE",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(discardCourseApi);
    yield put(discardingCourseDraftSucceeded({ response: response }));
  } catch (error) {
    yield put(discardingCourseDraftFailed({ id: id, error: error }));
  }
};

// publish course
export const publishCourse = createAction<string>(PUBLISH_COURSE_REQUEST);
const publishingCourseSucceeded = createAction<PublishCourseResponse>(
  PUBLISHING_COURSE_SUCCEEDED,
);
const publishingCourseFailed = createAction<PublishCourseErrorResponse>(
  PUBLISHING_COURSE_FAILED,
);

const publishCourseSaga = function* (
  action: ReturnType<typeof publishCourse>,
): any {
  const id = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/courses/${id}/publish`,
  });

  const publishCourseApi = () => {
    return axios({
      method: "POST",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(publishCourseApi);
    yield put(publishingCourseSucceeded({ response: response }));
  } catch (error) {
    yield put(publishingCourseFailed({ id: id, error: error }));
  }
};

export const invalidatePublishCourse = createAction(INVALIDATE_PUBLISH_COURSE);

export const editCourse = createAction<Course>(EDIT_COURSE_REQUEST);

export const validateCourse = createAction<string>(VALIDATE_COURSE_REQUEST);
export const updateValidationErrorsForCourse =
  createAction<UpdateValidationErrorsForCourseRequest>(
    UPDATE_VALIDATION_ERRORS_FOR_COURSE,
  );

const validateCourseSaga = function* (
  action: ReturnType<typeof validateCourse>,
): any {
  const id = action.payload;
  const course = yield select((state) => state.courses.edit?.item);

  if (course && course.id === id) {
    const errors = validateCourseRequest(course);
    yield put(
      updateValidationErrorsForCourse({ course: course.id!, errors: errors }),
    );
  }
};

export const addAssetToCourse =
  createAction<AddAssetToCourseRequest>(ADD_ASSET_TO_COURSE);
export const removeEntityFromCourse =
  createAction<RemoveEntityFromCourseRequest>(REMOVE_ENTITY_FROM_COURSE);
export const rearrangeEntitiesInCourse =
  createAction<RearrangeEntitiesInCourseRequest>(REARRANGE_ENTITIES_IN_COURSE);

export const updateCourseProperty = createAction<UpdateCoursePropertyRequest>(
  UPDATE_COURSE_PROPERTY_REQUEST,
);
export const updateLessonProperty = createAction<UpdateLessonPropertyRequest>(
  UPDATE_LESSON_PROPERTY_REQUEST,
);

// create assessment
export const createAssessment = createAction<CreateAssessmentRequest>(
  CREATE_ASSESSMENT_REQUEST,
);
export const updateAssessmentProperty =
  createAction<UpdateAssessmentPropertyRequest>(
    UPDATE_ASSESSMENT_PROPERTY_REQUEST,
  );

export const createQuestionInAssessment =
  createAction<CreateQuestionInAssessmentRequest>(
    CREATE_QUESTION_IN_ASSESSMENT_REQUEST,
  );
export const updateQuestionInAssessment =
  createAction<UpdateQuestionInAssessmentRequest>(
    UPDATE_QUESTION_IN_ASSESSMENT_REQUEST,
  );
export const deleteQuestionInAssessment =
  createAction<DeleteQuestionInAssessmentRequest>(
    DELETE_QUESTION_IN_ASSESSMENT_REQUEST,
  );
export const rearrangeQuestionsInAssessment =
  createAction<RearrangeQuestionsInAssessmentRequest>(
    REARRANGE_QUESTIONS_IN_ASSESSMENT_REQUEST,
  );

export const invalidateCourses = createAction<void>(INVALIDATE_COURSES);
export const invalidateCourseCreationState = createAction<void>(INVALIDATE_COURSE_CREATION_STATE);
export const invalidateCourseUpdateState = createAction<void>(
  INVALIDATE_COURSE_UPDATE_STATE,
);
export const invalidateCourseDeletionState = createAction<string>(
  INVALIDATE_COURSE_DELETION_STATE,
);
export const deleteCourseContent = createAction<any>(DELETE_COURSE_CONTENT);

// generate questions
export const generateQuestions = createAction<GenerateQuestionsRequest>(GENERATE_QUESTIONS_REQUEST);
export const generateQuestionsSuccedded = createAction<GenerateQuestionsResponse>(GENERATING_QUESTIONS_SUCCEEDED);
export const generateQuestionsFailed = createAction<GenerateQuestionsErrorResponse>(GENERATING_QUESTIONS_FAILED);

const generateQuestionsSaga = function* (action: ReturnType<typeof generateQuestions>): any {
  const {course, questions, assets} = action.payload as GenerateQuestionsRequest;

  const url = buildUrl(apiUrl, {
    path: `/api/1.0/seek/questions`
  });

  const data = {
    questions: questions,
    assets: assets
  }

  const generateQuestionsAPI = () => {
    return axios({
      method: 'POST',
      url: url,
      data: data
    }).then((response) => response.data);
  };

  try {
    const response = yield call(generateQuestionsAPI);
    yield put(generateQuestionsSuccedded({
      course: course,
      responses: response
    }));
  } catch (error) {
    yield put(generateQuestionsFailed({
      course: course,
      error: error
    }));
  }
}

export const invalidateGenerateQuestions =  createAction<string>(INVALIDATE_GENERATE_QUESTIONS);

// fetch lesson for course
export const fetchLessonForCourse = createAction<FetchLessonForCourseRequest>(FETCH_LESSON_FOR_COURSE_REQUEST);
const fetchingLessonForCourseSucceeded = createAction<FetchLessonForCourseResponse>(FETCHING_LESSON_FOR_COURSE_SUCCEEDED);
const fetchingLessonForCourseFailed = createAction<FetchLessonForCourseErrorResponse>(FETCHING_LESSON_FOR_COURSE_FAILED);

const fetchLessonForCourseSaga = function* (action: ReturnType<typeof fetchLessonForCourse>): any {
  const { course, lesson, asset } = action.payload as FetchLessonForCourseRequest;

  const url = buildUrl(apiUrl, {
    path: `/api/1.0/courses/${course}/lessons`,
    queryParams: {
      lesson: lesson,
      asset: asset
    }
  });

  const fetchLessonForCourseApi = () => {
    return axios({
      method: 'GET',
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchLessonForCourseApi);
    yield put(fetchingLessonForCourseSucceeded({
      course: course,
      lesson: lesson,
      asset: response
    }));
  } catch (error) {
    yield put(fetchingLessonForCourseFailed({
      course: course,
      lesson: lesson,
      asset: asset,
      error: error
    }));
  }
}

// fetch lesson preview for course
export const fetchLessonPreviewForCourse = createAction<FetchLessonPreviewForCourseRequest>(FETCH_LESSON_PREVIEW_FOR_COURSE_REQUEST);
const fetchingLessonPreviewForCourseSucceeded = createAction<FetchLessonPreviewForCourseResponse>(FETCHING_LESSON_PREVIEW_FOR_COURSE_SUCCEEDED);
const fetchingLessonPreviewForCourseFailed = createAction<FetchLessonPreviewForCourseErrorResponse>(FETCHING_LESSON_PREVIEW_FOR_COURSE_FAILED);

const fetchLessonPreviewForCourseSaga = function* (action: ReturnType<typeof fetchLessonForCourse>): any {
  const { course, lesson, asset, includeText } = action.payload as FetchLessonPreviewForCourseRequest;

  const url = buildUrl(apiUrl, {
    path: `/api/1.0/courses/${course}/lessons/preview`,
    queryParams: {
      lesson: lesson,
      asset: asset,
      includeText: "" + includeText
    }
  });

  const fetchLessonPreviewForCourseApi = () => {
    return axios({
      method: 'GET',
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchLessonPreviewForCourseApi);
    yield put(fetchingLessonPreviewForCourseSucceeded({
      course: course,
      lesson: lesson,
      asset: asset,
      preview: response
    }));
  } catch (error) {
    yield put(fetchingLessonPreviewForCourseFailed({
      course: course,
      lesson: lesson,
      asset: asset,
      error: error
    }));
  }
}

export default function* coursesSaga() {
  yield takeEvery(SEARCH_COURSES_REQUEST, searchCoursesSaga);
  yield takeEvery(FETCHING_DRAFT_COURSES_REQUEST, fetchDraftCoursesSaga);
  yield takeEvery(FETCHING_PUBLISHED_COURSES_REQUEST,fetchPublishedCoursesSaga);
  yield takeEvery(UPDATING_COURSE_REQUEST, updateCourseSaga);
  yield takeEvery(VALIDATE_COURSE_REQUEST, validateCourseSaga);
  yield takeEvery(DELETE_COURSE_REQUEST, deleteCourseSaga);
  yield takeEvery(DISCARD_COURSE_DRAFT_REQUEST, discardCourseDraftSaga);
  yield takeEvery(FETCH_COURSE_REQUEST, fetchCourseSaga);
  yield takeEvery(FETCH_DRAFT_COURSE_REQUEST, fetchDraftCourseSaga);
  yield takeEvery(CREATE_COURSE_REQUEST, createCourseSaga);
  yield takeEvery(PUBLISH_COURSE_REQUEST, publishCourseSaga);
  yield takeEvery(GENERATE_QUESTIONS_REQUEST, generateQuestionsSaga);
  yield takeEvery(FETCH_LESSON_FOR_COURSE_REQUEST, fetchLessonForCourseSaga);
  yield takeEvery(FETCH_LESSON_PREVIEW_FOR_COURSE_REQUEST, fetchLessonPreviewForCourseSaga);
}
